import { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

const FALLBACK = [
  { _id: "t1", client_name: "L. Bennett", client_title: "Executive Director", organization: "Atlas Foundation", quote: "Upper Crest Image transformed how the world receives our mission. The clarity, the polish — flawless.", rating: 5 },
  { _id: "t2", client_name: "Rev. M. Carter", client_title: "Senior Pastor", organization: "Sovereign Sanctuary", quote: "I came to them for image consulting. I left with a complete strategic framework for influence.", rating: 5 },
  { _id: "t3", client_name: "Sen. R. Holloway", client_title: "Campaign Principal", organization: "Hampton Roads", quote: "Our campaign moved from competent to commanding the moment they took the brand reins.", rating: 5 },
  { _id: "t4", client_name: "K. Vasquez", client_title: "Founder", organization: "Pinnacle Group", quote: "Editorial taste meets operational discipline. A rare combination — and it shows in everything they touch.", rating: 5 },
];

export default function TestimonialsPage() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ client_name: "", client_title: "", organization: "", quote: "", rating: 5 });
  const [submitting, setSubmitting] = useState(false);
  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data)).catch(() => setItems([]));
  }, []);
  const list = items.length ? items : FALLBACK;

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/testimonials", form);
      toast.success("Thank you — your testimonial has been submitted for review.");
      setForm({ client_name: "", client_title: "", organization: "", quote: "", rating: 5 });
    } catch {
      toast.error("Could not submit — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="testimonials-page">
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pt-28 md:pt-40 pb-12">
        <div className="eyebrow text-[var(--crest-gold)]">— Testimonials</div>
        <h1 className="font-serif text-5xl md:text-7xl tracking-tight mt-6 leading-[1.02] max-w-3xl">Words from the principals.</h1>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 md:px-10 pb-24 grid md:grid-cols-2 gap-px bg-black/10 border border-black/10">
        {list.map((t, i) => (
          <div key={t._id || i} className="bg-white p-10" data-testid={`testimonial-${i}`}>
            <div className="flex gap-1">
              {Array.from({ length: t.rating || 5 }).map((_, k) => (
                <Star key={k} size={14} fill="currentColor" style={{ color: "var(--crest-gold)" }} />
              ))}
            </div>
            <p className="font-serif text-2xl md:text-3xl leading-snug mt-6">"{t.quote}"</p>
            <div className="mt-8">
              <div className="font-medium">{t.client_name}</div>
              <div className="text-xs uppercase tracking-[0.22em] text-[var(--crest-muted)] mt-1">
                {t.client_title}{t.organization ? ` · ${t.organization}` : ""}
              </div>
            </div>
          </div>
        ))}
      </section>

      <section className="bg-[var(--crest-black)] text-[var(--crest-cream)] on-dark">
        <div className="max-w-[1000px] mx-auto px-6 md:px-10 py-24 md:py-32">
          <div className="eyebrow" style={{ color: "var(--crest-gold)" }}>— Submit a Review</div>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight mt-4">Share your experience.</h2>
          <form onSubmit={submit} className="mt-12 grid md:grid-cols-2 gap-x-10 gap-y-2" data-testid="testimonial-form">
            <input className="crest-input" placeholder="Your name" required value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="testimonial-name" />
            <input className="crest-input" placeholder="Title" value={form.client_title} onChange={(e) => setForm({ ...form, client_title: e.target.value })} data-testid="testimonial-title" />
            <input className="crest-input md:col-span-2" placeholder="Organization (optional)" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} data-testid="testimonial-org" />
            <textarea className="crest-input md:col-span-2" placeholder="Your testimonial..." required value={form.quote} onChange={(e) => setForm({ ...form, quote: e.target.value })} data-testid="testimonial-quote" />
            <div className="md:col-span-2 mt-4 flex items-center gap-4">
              <span className="eyebrow" style={{ color: "var(--crest-gold)" }}>Rating</span>
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button type="button" key={n} onClick={() => setForm({ ...form, rating: n })} className="p-1" data-testid={`rating-${n}`}>
                    <Star size={20} fill={n <= form.rating ? "currentColor" : "none"} style={{ color: "var(--crest-gold)" }} />
                  </button>
                ))}
              </div>
            </div>
            <button className="btn-primary md:col-span-2 mt-6" style={{ background: "var(--crest-gold)", color: "var(--crest-black)", borderColor: "var(--crest-gold)" }} disabled={submitting} data-testid="testimonial-submit">
              {submitting ? "Submitting..." : "Submit Testimonial"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
